import React from 'react';
import PropTypes from 'prop-types';
import { QUARTER_LABELS, getQuarterState } from '../../utils/gameDetailUtils';
import styles from './GameDetailModal.module.css';

const quarterCellClass = (state) => {
  switch (state) {
    case 'active':
      return styles.quarterActive;
    case 'completed':
      return styles.quarterCompleted;
    default:
      return styles.quarterFuture;
  }
};

const quarterHeaderClass = (state) => {
  switch (state) {
    case 'active':
      return styles.quarterHeaderActive;
    case 'completed':
      return styles.quarterHeaderCompleted;
    default:
      return styles.quarterHeaderFuture;
  }
};

const GameDetailBoxScore = ({ game }) => {
  const homeScores = game.home_line_scores || [];
  const awayScores = game.away_line_scores || [];
  const maxQuarters = Math.max(homeScores.length, awayScores.length, 0);

  if (maxQuarters === 0) {
    return (
      <div className={styles.section}>
        <p className={styles.emptyState}>
          {game.game_status === 'scheduled'
            ? 'Box score will appear once the game starts.'
            : 'Quarter scores are not available yet.'}
        </p>
      </div>
    );
  }

  const quarterLabels = QUARTER_LABELS.slice(0, maxQuarters);
  const quarterStates = quarterLabels.map((_, index) => (
    getQuarterState(index, game.period, game.clock, game.game_status)
  ));

  const renderScoreCell = (scores, index, state) => {
    const hasScore = scores[index] != null;
    if (state === 'future' && !hasScore) return '—';
    return hasScore ? scores[index] : '—';
  };

  return (
    <div className={styles.section}>
      {game.game_status === 'in_progress' && (
        <p className={styles.boxScoreLegend}>
          <span className={styles.legendActive}>■</span> In progress
          <span className={styles.legendCompleted}>■</span> Final
        </p>
      )}
      <table className={styles.boxScoreTable}>
        <thead>
          <tr>
            <th>Team</th>
            {quarterLabels.map((label, index) => (
              <th
                key={label}
                className={quarterHeaderClass(quarterStates[index])}
                aria-current={quarterStates[index] === 'active' ? 'true' : undefined}
              >
                {label}
                {quarterStates[index] === 'active' && (
                  <span className={styles.quarterLiveDot} aria-hidden>●</span>
                )}
              </th>
            ))}
            <th>T</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{game.away_team.abbreviation || game.away_team.name}</td>
            {quarterLabels.map((label, index) => (
              <td
                key={`away-${label}`}
                className={quarterCellClass(quarterStates[index])}
              >
                {renderScoreCell(awayScores, index, quarterStates[index])}
              </td>
            ))}
            <td className={styles.totalCell}>{game.away_score ?? '—'}</td>
          </tr>
          <tr>
            <td>{game.home_team.abbreviation || game.home_team.name}</td>
            {quarterLabels.map((label, index) => (
              <td
                key={`home-${label}`}
                className={quarterCellClass(quarterStates[index])}
              >
                {renderScoreCell(homeScores, index, quarterStates[index])}
              </td>
            ))}
            <td className={styles.totalCell}>{game.home_score ?? '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

GameDetailBoxScore.propTypes = {
  game: PropTypes.object.isRequired,
};

export default GameDetailBoxScore;
